import { useRef, useState } from 'react'
import type { NavItem } from '@assessments/shared'
import { cn } from '@/lib/utils'
import { NavDropdown } from './NavDropdown.js'
import { NavPanel } from './NavPanel.js'
import { NavLockIcon } from './NavLockIcon.js'

interface Props {
  item: NavItem
  isActive?: boolean
  activeChildIndex?: number | null
}

// `.nav-item` wrapper: full-height cell with the 3px transparent underline the active state fills.
const itemWrap = 'relative flex h-16 items-center border-b-[3px] border-transparent'
// `.nav-trigger`: compact padded pill mirroring the Platform class.
const trigger =
  'inline-flex items-center gap-2 whitespace-nowrap rounded px-3 py-2 font-jakarta text-sm font-medium leading-5 text-body'

// Matches the Platform dropdown min-width; used to decide whether the panel flips to the right edge.
const DROPDOWN_MIN_WIDTH = 650

function Chevron() {
  return (
    <svg
      className="h-4 w-4 transition-transform group-hover:rotate-180"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export function NavBarItem({ item, isActive = false, activeChildIndex = null }: Props) {
  // Only an <a>/<button> carries it; a disabled span is not a navigable destination.
  const current = isActive ? 'page' : undefined
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [flip, setFlip] = useState(false)

  // Mirror the Platform's flip: if a left-aligned dropdown would overflow the viewport, anchor it right.
  function measureFlip() {
    const el = wrapperRef.current
    if (!el) return
    setFlip(el.getBoundingClientRect().left + DROPDOWN_MIN_WIDTH > window.innerWidth)
  }

  if (item.state === 'disabled') {
    return (
      <div className={cn(itemWrap, isActive && 'border-highlight', 'group text-subtle')}>
        <span className={cn(trigger, 'cursor-not-allowed text-subtle')}>
          {item.label}
          {item.show_lock && <NavLockIcon />}
        </span>
        {item.panel && (
          <div className="absolute left-0 top-full z-[99999] mt-[3px] hidden group-hover:block">
            <NavPanel panel={item.panel} />
          </div>
        )}
      </div>
    )
  }

  if (item.children.length > 0) {
    return (
      <div
        ref={wrapperRef}
        className={cn(itemWrap, isActive && 'border-highlight', 'group')}
        onMouseEnter={measureFlip}
      >
        <button
          type="button"
          aria-current={current}
          className={cn(
            trigger,
            'cursor-pointer border-none bg-transparent hover:bg-surface-hover hover:text-heading',
          )}
        >
          {item.label}
          <Chevron />
        </button>
        <div
          className={cn(
            'absolute top-full z-[99999] mt-[3px] hidden group-hover:block',
            flip ? 'right-0' : 'left-0',
          )}
        >
          <NavDropdown items={item.children} activeIndex={activeChildIndex} />
        </div>
      </div>
    )
  }

  return (
    <div className={cn(itemWrap, isActive && 'border-highlight')}>
      {item.href ? (
        <a
          href={item.href}
          aria-current={current}
          className={cn(trigger, 'no-underline hover:bg-surface-hover hover:text-heading')}
        >
          {item.label}
        </a>
      ) : (
        <span className={cn(trigger, 'cursor-default text-subtle')}>{item.label}</span>
      )}
    </div>
  )
}
